/**
 * Chat Storage Utility - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive chat storage utility with advanced features:
 * - IndexedDB storage for Daire chat conversations
 * - Session context management for chat continuity
 * - Cross-instance synchronization via storage events
 * - Chat history persistence and retrieval
 * - Automatic chat titling from first user message
 * - Database schema management and upgrades
 * - Error handling for storage operations
 * - Chat session state management
 * 
 * Features:
 * - Persistent chat storage using IndexedDB
 * - Session context saving and retrieval
 * - Cross-tab/window chat synchronization
 * - Chat history with timestamps
 * - Automatic chat title generation
 * - Database versioning and migration
 * - Error handling for all operations
 * - Storage event broadcasting
 * 
 * Database Structure:
 * - 'chats' store: Individual chat conversations with messages
 * - 'sessionContext' store: Current session state and context
 * - Indexed by timestamp for chronological ordering
 * - Auto-incrementing IDs for unique chat identification
 * 
 * Storage Events:
 * - Broadcasts session context changes across browser instances
 * - Enables real-time sync between multiple portal tabs
 * - Custom storage events for cross-instance communication
 * 
 * @requires IndexedDB Browser database for persistence
 * @returns {Object} Chat storage methods for persistence and retrieval
 */
const DB_NAME = 'DairaChats';
const STORE_NAME = 'chats';
const CONTEXT_STORE = 'sessionContext';
const DB_VERSION = 2;

const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!db.objectStoreNames.contains(CONTEXT_STORE)) {
        db.createObjectStore(CONTEXT_STORE, { keyPath: 'id' });
      }
    };
  });
};

const saveChat = async (messages) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const chat = {
      messages,
      timestamp: new Date().toISOString(),
      title: messages[1]?.content?.slice(0, 30) + '...' || 'New Chat'
    };
    
    const request = store.add(chat);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const loadChats = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const loadChat = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Save session context
const saveSessionContext = async (context) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([CONTEXT_STORE], 'readwrite');
    const store = transaction.objectStore(CONTEXT_STORE);
    
    await store.put({
      id: 'current_session',
      ...context,
      timestamp: Date.now()
    });
    
    // Trigger storage event for cross-instance sync
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'daire_session_context',
      newValue: JSON.stringify(context)
    }));
  } catch (error) {
    console.error('Error saving session context:', error);
    throw error;
  }
};

// Get session context
const getSessionContext = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CONTEXT_STORE], 'readonly');
    const store = transaction.objectStore(CONTEXT_STORE);
    const request = store.get('current');
    
    request.onsuccess = () => resolve(request.result?.context || null);
    request.onerror = () => reject(request.error);
  });
};

// Clear session context
const clearSessionContext = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CONTEXT_STORE], 'readwrite');
    const store = transaction.objectStore(CONTEXT_STORE);
    const request = store.delete('current');
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export { saveChat, loadChats, loadChat, saveSessionContext, getSessionContext, clearSessionContext }; 